import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { mailApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback'
import { StatusPill } from '@/components/ui/StatusPill'
import { cn, initials, relativeTime } from '@/lib/utils'
import { toast } from '@/store/toast'
import { fromCurrent } from '@/features/applications/backTarget'
import type {
  MailAlias,
  MailCategory,
  MailMessage,
  MailMessageDetail,
  MailStats,
  PaginatedResponse,
} from '@/types/api'

import {
  FILTER_CATEGORIES,
  aliasReady,
  categoryMeta,
  copyText,
  retryUnlessMissing,
  useMailAlias,
  useMailStats,
} from './mailMeta'

const PAGE_SIZE = 20
type Filter = MailCategory | 'all'

/**
 * The application inbox: mail sent to the user's alias (and any connected
 * mailbox), sorted into categories, with what we did about each one.
 *
 * The open message lives in `?message=<id>`, so the application page can link
 * straight to one and the browser back button closes it on a phone. On phones
 * the list and the message are two stacked screens; from md they sit side by side.
 */
export function InboxView() {
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const selectedId = params.get('message')

  const [filter, setFilter] = useState<Filter>('all')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])
  useEffect(() => setPage(1), [filter, search])

  const aliasQuery = useMailAlias()
  const statsQuery = useMailStats(true)
  const listQuery = useQuery({
    queryKey: ['mail-messages', filter, search, page],
    queryFn: () =>
      mailApi.messages({
        category: filter === 'all' ? undefined : filter,
        search: search || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
    retry: retryUnlessMissing,
    refetchInterval: (query) => {
      const error = query.state.error
      return error instanceof ApiError && error.status === 404 ? false : 20_000
    },
  })

  const messages: MailMessage[] = Array.isArray(listQuery.data?.data) ? listQuery.data!.data : []
  const pageInfo = listQuery.data?.pageInfo
  const total = pageInfo?.totalItems ?? messages.length
  const selectedSummary = messages.find((message) => message.id === selectedId)

  const detailQuery = useQuery({
    queryKey: ['mail-message', selectedId],
    queryFn: () => mailApi.message(selectedId!),
    enabled: Boolean(selectedId),
    retry: retryUnlessMissing,
    staleTime: 60_000,
  })

  const markRead = useMutation({
    mutationFn: (id: string) => mailApi.markRead(id),
    onMutate: (id: string) => {
      queryClient.setQueriesData<PaginatedResponse<MailMessage>>({ queryKey: ['mail-messages'] }, (data) =>
        data && Array.isArray(data.data)
          ? { ...data, data: data.data.map((m) => (m.id === id ? { ...m, isRead: true } : m)) }
          : data,
      )
      queryClient.setQueryData<MailMessageDetail>(['mail-message', id], (data) =>
        data ? { ...data, isRead: true } : data,
      )
      queryClient.setQueryData<MailStats>(['mail-stats'], (data) =>
        data ? { ...data, unread: Math.max(0, (data.unread ?? 0) - 1) } : data,
      )
    },
    // Quietly resync rather than toasting: a read receipt is not worth an error.
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['mail-messages'] })
      queryClient.invalidateQueries({ queryKey: ['mail-stats'] })
    },
  })

  const markAllRead = useMutation({
    mutationFn: mailApi.markAllRead,
    onMutate: () => {
      queryClient.setQueriesData<PaginatedResponse<MailMessage>>({ queryKey: ['mail-messages'] }, (data) =>
        data && Array.isArray(data.data)
          ? { ...data, data: data.data.map((m) => ({ ...m, isRead: true })) }
          : data,
      )
      queryClient.setQueryData<MailStats>(['mail-stats'], (data) => (data ? { ...data, unread: 0 } : data))
    },
    onError: (error: ApiError) => {
      toast.error('Could not mark all as read', error.message)
      queryClient.invalidateQueries({ queryKey: ['mail-messages'] })
      queryClient.invalidateQueries({ queryKey: ['mail-stats'] })
    },
  })

  // Opening an unread message (by click or deep link) marks it read, once.
  const marked = useRef(new Set<string>())
  const openIsUnread =
    selectedSummary?.isRead === false || (detailQuery.data ? detailQuery.data.isRead === false : false)
  useEffect(() => {
    if (!selectedId || !openIsUnread || marked.current.has(selectedId)) return
    marked.current.add(selectedId)
    markRead.mutate(selectedId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, openIsUnread])

  const select = (id: string | null) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (id) next.set('message', id)
        else next.delete('message')
        return next
      },
      { replace: Boolean(id && selectedId) },
    )
  }

  const alias = aliasQuery.data
  const unread = statsQuery.data?.unread ?? messages.filter((m) => !m.isRead).length
  const filtered = filter !== 'all' || Boolean(search)
  const listMissing = listQuery.error instanceof ApiError && listQuery.error.status === 404
  const showSetup =
    !listQuery.isLoading &&
    messages.length === 0 &&
    !filtered &&
    (listMissing || !aliasReady(alias)) &&
    !aliasQuery.isLoading

  // On a phone the open message replaces the list (and its header).
  const listOnly = selectedId ? 'hidden md:flex' : 'flex'

  return (
    <section className="card overflow-hidden">
      <header className={cn('flex-col gap-2.5 border-b border-line px-3 py-3 sm:px-4', selectedId ? 'hidden md:flex' : 'flex')}>
        <div className="flex flex-wrap items-start justify-between gap-2 pb-2">
          <div>
            <h2 className="text-[17px] font-semibold text-ink">Employer inbox</h2>
            <p className="mt-1 text-[13px] text-ink-muted">Verification emails, application updates, and interview invitations in one place.</p>
          </div>
          <span className="rounded-pill bg-brand-soft px-2.5 py-1 text-[12px] font-medium text-brand">{unread} unread</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SearchBox value={searchInput} onChange={setSearchInput} />
          <p className="num hidden shrink-0 text-[13px] text-ink-muted sm:block">
            {unread > 0 ? `${unread} unread` : 'All caught up'}
          </p>
          <AliasLine alias={alias} loading={aliasQuery.isLoading} className="hidden min-w-0 lg:flex" />
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-10 shrink-0 md:h-9"
            disabled={unread === 0 || messages.length === 0}
            loading={markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            Mark all read
          </Button>
        </div>
        <AliasLine alias={alias} loading={aliasQuery.isLoading} className="lg:hidden" />
        <div className="hscroll -mx-3 gap-1.5 px-3 py-0.5 sm:-mx-4 sm:px-4 md:mx-0 md:flex-wrap md:px-0">
          <CategoryChips active={filter} stats={statsQuery.data} onChange={setFilter} />
        </div>
      </header>

      {showSetup ? (
        <SetupState alias={alias} />
      ) : (
        <div className="md:grid md:h-[calc(100dvh-440px)] md:min-h-[480px] md:grid-cols-[minmax(300px,360px)_minmax(0,1fr)]">
          <div className={cn('min-h-0 flex-col md:flex md:border-r md:border-line', listOnly)}>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {listQuery.isLoading ? (
                <ListSkeleton />
              ) : listQuery.isError && messages.length === 0 && !listMissing ? (
                <ErrorState description="We could not load your inbox." onRetry={() => listQuery.refetch()} />
              ) : messages.length === 0 ? (
                filtered ? (
                  <EmptyState
                    title="Nothing matches"
                    description="Try another category or clear the search."
                    action={
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setFilter('all')
                          setSearchInput('')
                        }}
                      >
                        Show all mail
                      </Button>
                    }
                  />
                ) : (
                  <EmptyState
                    icon={<MailIcon className="h-8 w-8" />}
                    title="No emails yet"
                    description={
                      alias?.address
                        ? `Mail employers send to ${alias.address} will show up here.`
                        : 'Mail employers send about your applications will show up here.'
                    }
                  />
                )
              ) : (
                <ul className="divide-y divide-line">
                  {messages.map((message) => (
                    <li key={message.id}>
                      <MessageRow
                        message={message}
                        selected={message.id === selectedId}
                        onSelect={() => select(message.id)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {messages.length > 0 && (
              <Pagination
                page={pageInfo?.page ?? page}
                pageSize={pageInfo?.pageSize ?? PAGE_SIZE}
                count={messages.length}
                total={total}
                hasPrevious={pageInfo?.hasPrevious ?? page > 1}
                hasNext={pageInfo?.hasNext ?? false}
                onPage={setPage}
              />
            )}
          </div>

          <div className={cn('min-h-0 overflow-y-auto', selectedId ? 'block' : 'hidden md:block')}>
            {selectedId ? (
              <ReadingPane
                summary={selectedSummary}
                detail={detailQuery.data}
                loading={detailQuery.isLoading}
                missing={detailQuery.isError && !selectedSummary}
                onBack={() => select(null)}
              />
            ) : (
              <div className="grid h-full place-items-center">
                <EmptyState
                  icon={<MailIcon className="h-8 w-8" />}
                  title="Select an email"
                  description="Pick a message on the left to read it here."
                />
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Header pieces
// ---------------------------------------------------------------------------
function CategoryChips({
  active,
  stats,
  onChange,
}: {
  active: Filter
  stats: MailStats | undefined
  onChange: (filter: Filter) => void
}) {
  const chips: Array<{ value: Filter; label: string; dot?: string }> = [
    { value: 'all', label: 'All' },
    ...FILTER_CATEGORIES.map((category) => {
      const meta = categoryMeta(category)
      return { value: category, label: meta.label, dot: meta.dot }
    }),
  ]
  return (
    <div role="tablist" aria-label="Filter by category" className="flex w-max gap-1.5 md:w-auto md:flex-wrap">
      {chips.map((chip) => {
        const selected = chip.value === active
        const count = chip.value === 'all' ? undefined : stats?.byCategory?.[chip.value]
        return (
          <button
            key={chip.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(chip.value)}
            className={cn(
              'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-pill px-3 text-[13px] font-medium transition-colors md:h-8',
              selected ? 'bg-ink text-oncolor' : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
            )}
          >
            {chip.dot && <span className={cn('h-1.5 w-1.5 rounded-full', selected ? 'bg-oncolor' : chip.dot)} />}
            {chip.label}
            {typeof count === 'number' && count > 0 && (
              <span className={cn('num', selected ? 'opacity-70' : 'text-ink-faint')}>{count}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/** "you@jobslide.app · Active": where employer mail lands, and whether it is flowing. */
function AliasLine({ alias, loading, className }: { alias: MailAlias | undefined; loading: boolean; className?: string }) {
  if (loading) return <Skeleton className={cn('h-4 w-56', className)} />
  if (!alias?.address) return null
  const active = aliasReady(alias) && alias.enabled
  return (
    <div className={cn('flex min-w-0 items-center gap-2 text-[12px] text-ink-muted', className)}>
      <MailIcon className="h-3.5 w-3.5 shrink-0 text-ink-faint" />
      <span className="num min-w-0 truncate" title={alias.address}>
        {alias.address}
      </span>
      <StatusPill size="sm" tone={active ? 'brand' : 'neutral'} live={active}>
        {active ? 'Active' : alias.domainConfigured ? 'Paused' : 'Not configured'}
      </StatusPill>
    </div>
  )
}

function SearchBox({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="relative min-w-0 flex-1 md:max-w-[320px]">
      <svg
        className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-faint"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="6" cy="6" r="4.2" stroke="currentColor" strokeWidth="1.5" />
        <path d="m9.3 9.3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search mail…"
        aria-label="Search mail"
        className="field-box h-10 w-full py-1.5 pl-8 text-[14px] md:h-9"
      />
    </div>
  )
}

function SetupState({ alias }: { alias: MailAlias | undefined }) {
  const adminPending = alias && !alias.domainConfigured
  return (
    <EmptyState
      icon={
        <span className="grid h-12 w-12 place-items-center rounded-full bg-brand-soft text-brand">
          <MailIcon className="h-5 w-5" />
        </span>
      }
      title="Set up your application email"
      description={
        adminPending
          ? 'Your administrator has not configured the mail domain yet. Once it is ready, employer mail (verification codes, interview invites, rejections) lands here by itself. You can also connect Gmail or Outlook.'
          : 'We give you an address to apply with, so verification codes are used for you and replies land here. You can also connect Gmail or Outlook.'
      }
      action={
        <Link
          to="/dashboard/settings#application-email"
          className="inline-flex h-10 items-center rounded-field bg-ink px-4 text-[13px] font-medium text-oncolor transition hover:bg-ink/90"
        >
          Open email settings
        </Link>
      }
    />
  )
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------
const AVATAR_TINTS = [
  'bg-info/12 text-info',
  'bg-brand-soft text-brand',
  'bg-warning/14 text-warning',
  'bg-danger/10 text-danger',
  'bg-accent-soft text-ink-soft',
]

function SenderAvatar({ name, size = 'md' }: { name: string; size?: 'md' | 'lg' }) {
  const tint = AVATAR_TINTS[[...name].reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % AVATAR_TINTS.length]
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid shrink-0 place-items-center rounded-full font-semibold',
        size === 'lg' ? 'h-10 w-10 text-[14px]' : 'h-9 w-9 text-[12.5px]',
        tint,
      )}
    >
      {initials(name) || '?'}
    </span>
  )
}

function senderName(message: Pick<MailMessage, 'fromName' | 'fromAddress' | 'companyName'>) {
  return message.fromName?.trim() || message.companyName || message.fromAddress || 'Unknown sender'
}

/** sender · time / subject / snippet · category */
function MessageRow({
  message,
  selected,
  onSelect,
}: {
  message: MailMessage
  selected: boolean
  onSelect: () => void
}) {
  const meta = categoryMeta(message.category)
  const name = senderName(message)
  const unread = !message.isRead
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected || undefined}
      className={cn(
        'relative flex w-full gap-3 px-4 py-3 text-left transition-colors',
        selected ? 'bg-surface-muted' : 'hover:bg-surface-muted/60 active:bg-surface-muted',
      )}
    >
      {selected && <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-ink" />}
      <SenderAvatar name={name} />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className={cn('truncate text-[13.5px]', unread ? 'font-semibold text-ink' : 'text-ink-soft')}>{name}</span>
          <span className="num flex shrink-0 items-center gap-1.5 text-[12px] text-ink-faint">
            {relativeTime(message.receivedAt)}
            {unread && <span className="h-2 w-2 rounded-full bg-brand" aria-label="Unread" />}
          </span>
        </span>
        <span className={cn('mt-0.5 block truncate text-[14px] leading-snug', unread ? 'font-semibold text-ink' : 'text-ink-soft')}>
          {message.subject || '(no subject)'}
        </span>
        <span className="mt-1 flex items-center justify-between gap-2">
          <span className="min-w-0 truncate text-[12.5px] text-ink-muted">{message.snippet || ' '}</span>
          <StatusPill size="sm" tone={meta.tone}>
            {meta.label}
          </StatusPill>
        </span>
      </span>
    </button>
  )
}

function ListSkeleton() {
  return (
    <div className="divide-y divide-line">
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className="flex gap-3 px-4 py-4">
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-1/2" />
            <Skeleton className="h-3 w-4/5" />
            <Skeleton className="h-3 w-3/5" />
          </div>
        </div>
      ))}
    </div>
  )
}

function Pagination({
  page,
  pageSize,
  count,
  total,
  hasPrevious,
  hasNext,
  onPage,
}: {
  page: number
  pageSize: number
  count: number
  total: number
  hasPrevious: boolean
  hasNext: boolean
  onPage: (page: number) => void
}) {
  const from = (page - 1) * pageSize + 1
  const to = from + count - 1
  return (
    <footer className="flex items-center justify-between gap-3 border-t border-line px-4 py-2">
      <p className="num text-[12.5px] text-ink-muted">
        {from}–{to} of {total}
      </p>
      <div className="flex gap-1">
        <PageButton label="Previous page" disabled={!hasPrevious} onClick={() => onPage(page - 1)}>
          <path d="m8.5 3.5-4 3.5 4 3.5" />
        </PageButton>
        <PageButton label="Next page" disabled={!hasNext} onClick={() => onPage(page + 1)}>
          <path d="m5.5 3.5 4 3.5-4 3.5" />
        </PageButton>
      </div>
    </footer>
  )
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="grid h-10 w-10 place-items-center rounded-field border border-line text-ink-soft transition hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-40 md:h-8 md:w-8"
    >
      <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
      </svg>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Reading pane
// ---------------------------------------------------------------------------
function formatFull(iso: string | null | undefined) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const SOURCE_LABEL: Record<string, string> = { gmail: 'Gmail', outlook: 'Outlook' }

function BackToInbox({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="-ml-2 mb-3 inline-flex h-10 items-center gap-1.5 rounded-field px-2 text-[13px] font-medium text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink md:hidden"
    >
      <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path d="m8.5 3.5-4 3.5 4 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Inbox
    </button>
  )
}

function ReadingPane({
  summary,
  detail,
  loading,
  missing,
  onBack,
}: {
  summary: MailMessage | undefined
  detail: MailMessageDetail | undefined
  loading: boolean
  missing: boolean
  onBack: () => void
}) {
  const navigate = useNavigate()
  const message: MailMessage | undefined = detail ?? summary

  if (!message) {
    return (
      <div className="px-4 py-4 sm:px-7 sm:py-5">
        <BackToInbox onBack={onBack} />
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="mt-6 h-48 w-full" />
          </div>
        ) : (
          <EmptyState
            title="This email could not be found"
            description={missing ? 'It may have been removed, or the inbox is not available yet.' : undefined}
          />
        )}
      </div>
    )
  }

  const meta = categoryMeta(message.category)
  const name = senderName(message)
  const application = detail?.application
  const linkedId = application?.id ?? message.applicationId
  const linkedLabel = application
    ? `${application.companyName} – ${application.title}`
    : message.companyName ?? 'application'
  const source = SOURCE_LABEL[message.source]

  return (
    <article className="px-4 py-4 sm:px-7 sm:py-6">
      <BackToInbox onBack={onBack} />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <h2 className="text-[18px] font-semibold leading-snug tracking-tight text-ink sm:text-[20px]">
          {message.subject || '(no subject)'}
        </h2>
        <StatusPill tone={meta.tone} className="self-start">
          {meta.label}
        </StatusPill>
      </div>

      <div className="mt-4 flex items-start gap-3">
        <SenderAvatar name={name} size="lg" />
        <div className="min-w-0 flex-1">
          <p className="break-words text-[14px] text-ink">
            <span className="font-medium">{name}</span>{' '}
            {message.fromAddress && <span className="text-ink-muted">&lt;{message.fromAddress}&gt;</span>}
          </p>
          <p className="mt-0.5 break-words text-[13px] text-ink-muted">
            to {message.toAddress || 'you'}
            {source && <span className="text-ink-faint"> · via {source}</span>}
          </p>
        </div>
        <time dateTime={message.receivedAt} className="num hidden shrink-0 text-[12.5px] text-ink-faint sm:block">
          {formatFull(message.receivedAt)}
        </time>
      </div>
      <p className="num mt-2 text-[12.5px] text-ink-faint sm:hidden">{formatFull(message.receivedAt)}</p>

      {(linkedId || detail?.forwardedAt) && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {linkedId && (
            <button
              type="button"
              onClick={() => navigate(`/dashboard/applications/${linkedId}`, { state: fromCurrent() })}
              className="inline-flex h-9 max-w-full items-center gap-1.5 rounded-pill border border-line bg-surface px-3 text-[12.5px] text-ink-soft transition hover:border-line-strong hover:text-ink"
            >
              <LinkIcon />
              <span className="truncate">
                Linked to <span className="font-medium text-ink">{linkedLabel}</span>
              </span>
            </button>
          )}
          {detail?.forwardedAt && (
            <span className="text-[12.5px] text-ink-faint">Forwarded to you {relativeTime(detail.forwardedAt)}</span>
          )}
        </div>
      )}

      {message.actionTaken && (
        <p className="mt-4 flex items-start gap-2.5 rounded-field bg-brand-soft px-3.5 py-2.5 text-[13.5px] text-brand">
          <CheckIcon />
          <span>{message.actionTaken}</span>
        </p>
      )}

      {detail?.verificationValue && (
        <VerificationValue
          kind={detail.verificationKind}
          value={detail.verificationValue}
          usedAt={detail.verificationUsedAt}
        />
      )}

      <div className="mt-5">
        {loading && !detail ? (
          <Skeleton className="h-56 w-full rounded-card" />
        ) : detail?.html ? (
          <EmailFrame html={detail.html} />
        ) : (
          <div className="whitespace-pre-wrap break-words rounded-card border border-line bg-surface-muted/60 px-4 py-4 text-[14px] leading-relaxed text-ink sm:px-5">
            {detail?.text || message.snippet || 'This email has no content.'}
          </div>
        )}
      </div>
    </article>
  )
}

function VerificationValue({
  kind,
  value,
  usedAt,
}: {
  kind: MailMessage['verificationKind']
  value: string
  usedAt: string | null
}) {
  const isLink = kind === 'link' || /^https?:\/\//i.test(value)
  const copy = async () => {
    if (await copyText(value)) toast.success(isLink ? 'Link copied' : 'Code copied')
    else toast.error('Could not copy', 'Select it and copy manually.')
  }
  return (
    <div className="mt-4 flex flex-col gap-3 rounded-field border border-line bg-surface-muted/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="label-eyebrow">{isLink ? 'Verification link' : 'Verification code'}</p>
        {isLink ? (
          <p className="num mt-1 truncate text-[13px] text-ink-soft" title={value}>
            {value}
          </p>
        ) : (
          <p className="num mt-1 text-[22px] font-semibold tracking-[0.18em] text-ink">{value}</p>
        )}
        {usedAt && <p className="mt-0.5 text-[12px] text-ink-faint">Used {relativeTime(usedAt)}</p>}
      </div>
      <Button variant="secondary" size="sm" onClick={copy} className="self-start sm:self-center">
        Copy
      </Button>
    </div>
  )
}

/**
 * Employer HTML in an iframe with scripts, forms and navigation disabled.
 * `allow-same-origin` (without `allow-scripts`) is only there so we can
 * measure the document and size the frame to it; nothing inside can run.
 *
 * The document inside is the employer's, laid out for a light page, so the
 * frame keeps its own light background in both themes (like any mail client).
 */
function EmailFrame({ html }: { html: string }) {
  const ref = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(160)
  const srcDoc = useMemo(
    () =>
      `<!doctype html><html><head><meta charset="utf-8">` +
      `<meta http-equiv="Content-Security-Policy" content="script-src 'none'; object-src 'none'; form-action 'none'">` +
      `<base target="_blank">` +
      `<style>html,body{margin:0;padding:0;background:#fff;color:#1f2937;` +
      `font:14px/1.6 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;}` +
      `body{padding:20px 22px;overflow-wrap:anywhere;}img{max-width:100%;height:auto;}table{max-width:100%!important;}` +
      `a{color:#0c8460;}</style></head><body>${html}</body></html>`,
    [html],
  )

  useEffect(() => {
    const frame = ref.current
    if (!frame) return
    let observer: ResizeObserver | undefined
    const measure = () => {
      try {
        const body = frame.contentDocument?.body
        if (!body) return
        const next = Math.ceil(body.getBoundingClientRect().height)
        if (next > 0) setHeight(next)
      } catch {
        // Cross-origin in some browsers: keep the fallback height.
      }
    }
    const onLoad = () => {
      measure()
      try {
        const doc = frame.contentDocument
        if (doc?.body && 'ResizeObserver' in window) {
          observer = new ResizeObserver(measure)
          observer.observe(doc.body)
        }
        doc?.querySelectorAll('img').forEach((img) => img.addEventListener('load', measure))
      } catch {
        /* ignore */
      }
    }
    frame.addEventListener('load', onLoad)
    return () => {
      frame.removeEventListener('load', onLoad)
      observer?.disconnect()
    }
  }, [srcDoc])

  return (
    <div className="overflow-hidden rounded-card border border-line bg-surface">
      <iframe
        ref={ref}
        title="Email content"
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        srcDoc={srcDoc}
        style={{ height }}
        className="block w-full border-0"
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------
export function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="m3 5.5 7 5.5 7-5.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg className="mt-[3px] h-3.5 w-3.5 shrink-0" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="m2.5 7.5 3 3 6-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function LinkIcon() {
  return (
    <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M6 8a2.5 2.5 0 0 0 3.5 0l2-2A2.5 2.5 0 0 0 8 2.5l-.7.7M8 6a2.5 2.5 0 0 0-3.5 0l-2 2A2.5 2.5 0 0 0 6 11.5l.7-.7"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}
